import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Card, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { useRequest } from "@components/AppHook";
import { DevForm } from "@components/Forms/Fields";
import { Awesome, OverflowArea } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { ListingCard } from "@components/Pages";
import { FindDuplsPublisherModal, doFindDuplsPublisher } from "@components/PublisherDuplicates";
import { parseAndLinkify, url_reverse } from "@components/Utils";

export default function PrModerationScreen() {
    const { t } = useTranslation();
    const params = useParams();
    const header = t("Пиар модерация");

    return (
        <DefaultLayout>
            {params.developer_id ? <Edit developerId={params.developer_id} /> : <List header={header} />}
        </DefaultLayout>
    );
}

function List({ header }) {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:moderation:pr:list"));

    if (!apiData) return;

    return (
        <ListingCard header={header}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Разработчик")}</th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((o) => (
                        <tr key={`object_list${o.id}`}>
                            <td>
                                <Link to={url_reverse("admin:moderation:pr:developer", { developer_id: o.id })}>
                                    {o.name}
                                </Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ListingCard>
    );
}

function Edit({ developerId }) {
    const { apiData } = useAppApi(url_reverse("api:moderation:pr:developer", { developer_id: developerId }));
    const navigate = useNavigate();

    useEffect(() => {
        let redirect = true;
        if (apiData?.status) {
            if (apiData.change_name) {
                redirect = false;
            }
            if (apiData.publisher_gamedev) {
                redirect = false;
            }
            if (redirect) {
                navigate(url_reverse("admin:moderation:pr:list"));
            }
        }
    }, [apiData]);

    if (!apiData) return;

    return (
        <>
            <ModerationPublishers />
            <ModerationChangeName />
        </>
    );
}

function ModerationPublishers() {
    const { t } = useTranslation();
    const { apiData, apiUrl, setApiData } = useAppContext();
    const [comment, setComment] = useState({});
    const { api_post } = useRequest();
    const [matchData, setMatchData] = useState(null);

    const handleSubmit = (ev, as_who) => {
        const oid = parseInt(ev.target.value);
        const state = ev.target.name;
        api_post(ev, apiUrl, {
            params: { action: "publisher_gamedev", id: oid, state, comment: comment[oid], as_who },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                }
            },
        });
    };

    const objDraw = (obj, as_who) => {
        return (
            <tr key={obj.id}>
                <td>{as_who === "publisher" ? t("Издатель") : t("Разработчик")}</td>
                <td>
                    <a
                        href={url_reverse("admin:publisher:developers", { publisher_id: obj.publisher.id })}
                        target="_blank"
                    >
                        {obj.publisher.name}
                    </a>
                    {" | "}
                    {obj.publisher.owner_id ? (
                        <a href={url_reverse("devs:edit", { dev_id: obj.publisher.owner_id })} target="_blank">
                            {t("Есть владелец")}
                        </a>
                    ) : (
                        t("Нет владельца")
                    )}
                    {" | "}
                    <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => doFindDuplsPublisher(obj.publisher.name, setMatchData)}
                    >
                        <Awesome type="search-plus" tooltip={t("Поиск совпадений")} />
                    </Button>
                </td>
                <td className="show-wrap-text">
                    <OverflowArea y={150}>{parseAndLinkify(obj.comment)} </OverflowArea>
                </td>
                <td>
                    <DevForm.TextArea
                        name="comment"
                        value={comment[obj.id]}
                        onChange={(ev) => setComment((prev) => ({ ...prev, [obj.id]: ev.target.value }))}
                        maxLength={1024}
                    />
                </td>
                <td>
                    <ButtonGroup>
                        <DevForm.Button
                            variant="success"
                            name="approved"
                            value={obj.id}
                            onClick={(ev) => handleSubmit(ev, as_who)}
                        >
                            {t("Принять")}
                        </DevForm.Button>
                        <DevForm.Button
                            variant="danger"
                            name="rejected"
                            value={obj.id}
                            onClick={(ev) => handleSubmit(ev, as_who)}
                        >
                            {t("Отклонить")}
                        </DevForm.Button>
                    </ButtonGroup>
                </td>
            </tr>
        );
    };

    return (
        apiData.publisher_gamedev && (
            <Card className="mb-3">
                <Card.Header>
                    <a href={url_reverse("devs:edit", { dev_id: apiData.developer.id })} target="_blank">
                        {apiData.developer.name}
                    </a>
                </Card.Header>
                <Card.Body>
                    <Table hover responsive className="dmg-table">
                        <thead>
                            <tr>
                                <th>{t("Справочник")}</th>
                                <th>{t("Значение")}</th>
                                <th style={{ width: "500px" }}>{t("Комментарий разработчика")}</th>
                                <th>
                                    {t("Комментарий модератора")} ({apiData.contact_lang})
                                </th>
                                <th style={{ width: "250px" }} />
                            </tr>
                        </thead>
                        <tbody>
                            {apiData.publisher_gamedev?.publisher_list?.map((obj) => objDraw(obj, "publisher"))}
                            {apiData.publisher_gamedev?.gamedev_list?.map((obj) => objDraw(obj, "gamedev"))}
                        </tbody>
                    </Table>
                    <FindDuplsPublisherModal data={matchData} setData={setMatchData} />
                </Card.Body>
            </Card>
        )
    );
}

function ModerationChangeName() {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppContext();
    const [comment, setComment] = useState("");

    const { api_post } = useRequest();

    const handleSubmit = (ev) => {
        api_post(ev, apiUrl, {
            params: { action: "change_name", state: ev.target.name, comment },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                }
            },
        });
    };

    return (
        apiData.change_name && (
            <Card className="mb-3">
                <Card.Header>
                    {t("Смена названия для")}{" "}
                    <a href={url_reverse("devs:edit", { dev_id: apiData.developer.id })} target="_blank">
                        {apiData.developer.name}
                    </a>
                </Card.Header>
                <Card.Body>
                    <Table hover responsive className="dmg-table">
                        <thead>
                            <tr>
                                <th>{t("Значение")}</th>
                                <th style={{ width: "500px" }}>{t("Комментарий разработчика")}</th>
                                <th>
                                    {t("Комментарий модератора")} ({apiData.contact_lang})
                                </th>
                                <th style={{ width: "250px" }} />
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{apiData.change_name.field_val}</td>
                                <td className="show-wrap-text">
                                    <OverflowArea y={150}>{parseAndLinkify(apiData.change_name.comment)} </OverflowArea>
                                </td>
                                <td>
                                    <DevForm.TextArea
                                        name="comment"
                                        value={comment}
                                        onChange={(ev) => setComment(ev.target.value)}
                                        maxLength={1024}
                                    />
                                </td>
                                <td>
                                    <ButtonGroup>
                                        <DevForm.Button variant="success" name="approved" onClick={handleSubmit}>
                                            {t("Принять")}
                                        </DevForm.Button>
                                        <DevForm.Button variant="danger" name="rejected" onClick={handleSubmit}>
                                            {t("Отклонить")}
                                        </DevForm.Button>
                                    </ButtonGroup>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        )
    );
}
